import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../core/auth/AuthContext";
import { getPushErrorMessage, usePushOptIn } from "../../features/notifications/usePushOptIn";
import { useModalAccessibility } from "../../hooks/useModalAccessibility";
import styles from "./PushNotificationBanner.module.css";

const INTRO_STORAGE_PREFIX = "karango_push_intro_v2";
const PARTICIPATION_STORAGE_PREFIX = "karango_push_participation_prompt_v1";
const FRESH_ACCOUNT_WINDOW_MS = 20 * 60 * 1000;

function storageKey(prefix, userId) {
  return `${prefix}:${String(userId || "unknown")}`;
}

function readStored(prefix, userId) {
  try {
    return window.localStorage.getItem(storageKey(prefix, userId)) === "1";
  } catch {
    return false;
  }
}

function persistStored(prefix, userId) {
  try {
    window.localStorage.setItem(storageKey(prefix, userId), "1");
  } catch {
    // La mémorisation améliore le confort mais ne doit jamais bloquer l'app.
  }
}

function isFreshAccount(user) {
  const creationTime = Date.parse(user?.metadata?.creationTime || "");
  const lastSignInTime = Date.parse(user?.metadata?.lastSignInTime || "");
  if (!Number.isFinite(creationTime)) return false;

  const accountIsRecent = Date.now() - creationTime <= FRESH_ACCOUNT_WINDOW_MS;
  const isFirstSession =
    !Number.isFinite(lastSignInTime) || Math.abs(lastSignInTime - creationTime) <= 2 * 60 * 1000;
  return accountIsRecent && isFirstSession;
}

export default function PushNotificationBanner() {
  const { user, authUser, isAuthenticated, isAnonymous } = useAuth();
  const { status, errorCode, activate, needsIosInstall, isBusy } = usePushOptIn();
  const [prompt, setPrompt] = useState(null);
  const openTimerRef = useRef(null);
  const dialogRef = useRef(null);
  const userId = authUser?.uid || user?.uid || user?.id || null;
  useModalAccessibility(dialogRef, { active: Boolean(prompt) });

  const canPrompt =
    Boolean(isAuthenticated && !isAnonymous && userId) &&
    status !== "granted" &&
    status !== "unsupported";

  useEffect(() => {
    window.clearTimeout(openTimerRef.current);
    if (!canPrompt || !isFreshAccount(authUser) || readStored(INTRO_STORAGE_PREFIX, userId)) {
      return undefined;
    }

    openTimerRef.current = window.setTimeout(() => setPrompt("signup"), 900);
    return () => window.clearTimeout(openTimerRef.current);
  }, [authUser, canPrompt, userId]);

  useEffect(() => {
    const handleParticipation = () => {
      if (!canPrompt || !userId || prompt) return;
      if (readStored(PARTICIPATION_STORAGE_PREFIX, userId)) return;
      persistStored(PARTICIPATION_STORAGE_PREFIX, userId);
      setPrompt("participation");
    };

    window.addEventListener("karango:participation-joined", handleParticipation);
    return () => window.removeEventListener("karango:participation-joined", handleParticipation);
  }, [canPrompt, prompt, userId]);

  useEffect(() => {
    if (status !== "granted") return;
    persistStored(INTRO_STORAGE_PREFIX, userId);
    setPrompt(null);
  }, [status, userId]);

  const copy = useMemo(() => {
    if (status === "browser-required") {
      return {
        title: "Ouvre Karango dans Chrome ou Safari",
        body: "Le navigateur intégré ne peut pas activer correctement les notifications.",
      };
    }
    if (needsIosInstall) {
      return {
        title: "Ajoute Karango à ton écran d’accueil",
        body: "Dans Safari, touche Partager (ou « … » puis Partager), puis « Sur l’écran d’accueil ». Si « Ouvrir comme app web » apparaît, active cette option, puis touche Ajouter. Ouvre ensuite Karango depuis son icône pour activer les notifications.",
      };
    }
    if (status === "denied") {
      return {
        title: "Notifications désactivées",
        body: "Tu peux les autoriser depuis les réglages de ton téléphone ou de ton navigateur.",
      };
    }
    if (prompt === "participation") {
      return {
        title: "Reçois les rappels de cette sortie",
        body: "Sois prévenu si l’horaire change ou si un nouveau message est publié.",
      };
    }
    return {
      title: "Ne manque aucune sortie",
      body: "Reçois tes nouveaux messages, tes rappels et les sorties qui pourraient te plaire.",
    };
  }, [needsIosInstall, prompt, status]);

  const closePrompt = useCallback(() => {
    if (prompt === "signup") persistStored(INTRO_STORAGE_PREFIX, userId);
    setPrompt(null);
  }, [prompt, userId]);

  useEffect(() => {
    if (!prompt) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closePrompt();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closePrompt, prompt]);

  if (!prompt || !canPrompt) return null;

  const canActivate =
    !needsIosInstall && status !== "browser-required" && status !== "denied";

  const handleActivate = async () => {
    if (!canActivate) {
      closePrompt();
      return;
    }
    const result = await activate();
    if (result?.success || result?.status === "idle" || result?.status === "denied") {
      persistStored(INTRO_STORAGE_PREFIX, userId);
      setPrompt(null);
    }
  };

  return (
    <div className={styles.overlay} role="presentation" onClick={closePrompt}>
      <section
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="push-prompt-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className={styles.close} onClick={closePrompt} aria-label="Fermer">×</button>
        <span className={styles.icon} aria-hidden="true">🔔</span>
        <div className={styles.copy}>
          <span className={styles.eyebrow}>Reste dans la boucle</span>
          <h2 id="push-prompt-title">{copy.title}</h2>
          <p>{copy.body}</p>
          {status === "error" && errorCode ? <small>{getPushErrorMessage(errorCode)}</small> : null}
        </div>
        <button type="button" className={styles.cta} onClick={handleActivate} disabled={isBusy}>
          {isBusy
            ? "Activation…"
            : canActivate
            ? status === "error" ? "Réessayer" : "Activer les notifications"
            : "J’ai compris"}
        </button>
        <button type="button" className={styles.later} onClick={closePrompt}>Plus tard</button>
      </section>
    </div>
  );
}
